import { Dialog, Switch, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import { capitalize, find } from "lodash";
import Tooltip from "rc-tooltip";
import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useAuthentication } from "../../context/authentication";
import { useBillingPlans } from "../../context/billingPlans";
import {
  isHistogramDataLoading,
  useFilteredList
} from "../../context/filteredList/FilteredListProvider";
import { useGoogleAnalytics } from "../../context/googleAnalytics";
import { useToast } from "../../context/toast";
import { useWorkspaces } from "../../context/workspaces/WorkspacesProvider";
import classNames from "../../utils/classNames";
import { PlanType } from "../../utils/types";
import useMapConfig, {
  relativePerformanceMetrics,
  VisualizationLayer
} from "../../utils/useMapConfig";
import CenteredLoading from "../CenteredLoading";
import HistogramChart from "../charts/HistogramChart";
import InfoIcon from "../icons/InfoIcon";

interface Props {
  open: boolean;
  onClose: () => void;
}

const MapAppearanceSettingsSidebar: React.FC<Props> = ({ open, onClose }) => {
  const visibleLayers = useMapConfig((state) => state.visibleLayers);
  const { trackEvent } = useGoogleAnalytics();
  const location = useLocation();
  const { user } = useAuthentication();
  const { currentWorkspace } = useWorkspaces();
  const { getPlanByType, setNewPlan } = useBillingPlans();
  const toggleVisibleLayer = useMapConfig((state) => state.toggleVisibleLayer);
  const pointColorMetricIteratee = useMapConfig(
    (state) => state.pointColorMetricIteratee
  );
  const setPointColorMetricIteratee = useMapConfig(
    (state) => state.setPointColorMetricIteratee
  );
  const { histogramDataMap } = useFilteredList();
  const { modalUpgradeConfig, setModalUpgradeConfig } = useToast();

  const timeTravelPageMatch = matchPath(location.pathname, {
    path: "/hotspots/time-travel"
  });

  const explorerPageMatch = matchPath<{
    tab?: string; // hotspots, locations, hexes, contacts
  }>(location.pathname, {
    path: "/:tab"
  });

  const superSimulationDetailsPageMatch = matchPath<{
    locationUuid: string;
    simulationUuid: string;
    subsection: string;
  }>(location.pathname, {
    path: "/locations/:locationUuid/super-simulations/:simulationUuid/:subsection"
  });

  const disableVisibleLayer = useCallback(() => {
    toggleVisibleLayer(VisualizationLayer.tooCloseToWitness, false);
    toggleVisibleLayer(VisualizationLayer.elevationContours, false);
    toggleVisibleLayer(VisualizationLayer.satellite, false);
  }, [toggleVisibleLayer]);

  useEffect(() => {
    if (!user) {
      disableVisibleLayer();
    }

    if (!currentWorkspace?.permissions) return;

    if (!currentWorkspace?.permissions["too-close-to-witness"]) {
      toggleVisibleLayer(VisualizationLayer.tooCloseToWitness, false);
    }

    if (!currentWorkspace?.permissions["elevation-contours"]) {
      toggleVisibleLayer(VisualizationLayer.elevationContours, false);
    }

    if (!currentWorkspace?.permissions["satellite-view"]) {
      toggleVisibleLayer(VisualizationLayer.satellite, false);
    }
  }, [currentWorkspace, user, toggleVisibleLayer, disableVisibleLayer]);

  const layers = useMemo(() => {
    const explorerTabEntity = explorerPageMatch?.params.tab as string;

    const allLayers = [
      {
        key: VisualizationLayer.cellPoints,
        title: "Helium 5G small cell radios",
        subtitle:
          "Visualize the areas where 5G small cell radios have been deployed.",
        hidden: false
      },
      {
        key: VisualizationLayer.problematicHexes,
        title: "Problematic hexes",
        subtitle:
          "These hexes lower the transmit scale as their limits are exceeded.",
        hidden:
          !!timeTravelPageMatch ||
          ["contacts", "installs", "hexes"].includes(explorerTabEntity)
      },
      {
        key: VisualizationLayer.tooCloseToWitness,
        title: "Too close to witness",
        subtitle:
          "Hotspots/locations that are too close to each other will not be rewarded for witnessing each other. These connections can be displayed by red lines.",
        hidden:
          !!timeTravelPageMatch ||
          ["contacts", "installs", "hexes"].includes(explorerTabEntity)
      },
      {
        key: VisualizationLayer.witnessLines,
        title: "Witness lines",
        subtitle:
          "Show/hide lines for hotspot witnesses. More info is on the hotspot witnesses tab.",
        hidden:
          explorerTabEntity !== "hotspots" && !superSimulationDetailsPageMatch
      },
      {
        key: VisualizationLayer.elevationContours,
        title: "Elevation contours",
        subtitle:
          "Contour lines show vertical dimension on a region by joining points of equal elevation. This makes it convenient (2D) to visually identify topological obstructions between hotspots/locations which can block coverage. It also identifies opportunities for better coverage by identifying hills.",
        hidden: !(
          explorerTabEntity &&
          ["hotspots", "locations"].includes(explorerTabEntity)
        )
      },
      {
        key: VisualizationLayer.hotspots,
        title: "Hotspots",
        subtitle:
          explorerTabEntity === "contacts"
            ? "Display hotspots that are currently installed at locations of your (filtered) contacts."
            : explorerTabEntity === "installs"
            ? "Display hotspots for your (filtered) installs"
            : "Hide hotspots for distraction free location planning.",
        hidden: explorerTabEntity === "hotspots"
      },
      {
        key: VisualizationLayer.locations,
        title: "Locations",
        subtitle: ["contacts", "installs"].includes(explorerTabEntity)
          ? `Display locations linked to your (filtered) ${explorerTabEntity}.`
          : "Locations allow managing and simulating potential places where hotspots can be installed at.",
        hidden: explorerTabEntity === "locations"
      },
      {
        key: VisualizationLayer.satellite,
        title: "Satellite view",
        subtitle: "Add a satellite view overlay to the map"
      }
    ];

    return allLayers.filter(({ hidden }) => !hidden);
  }, [
    explorerPageMatch?.params.tab,
    superSimulationDetailsPageMatch,
    timeTravelPageMatch
  ]);

  const checkCanshow = useCallback(() => {
    if ((open && modalUpgradeConfig.open) || open) return true;

    return false;
  }, [modalUpgradeConfig.open, open]);

  return (
    <Transition.Root show={checkCanshow()} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={checkCanshow()}
        onClose={() => {
          if (modalUpgradeConfig.open) return;

          onClose();
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md dark:text-white">
                <div className="max-w-3xl xl:max-w-6xl h-full">
                  <div className="h-full flex flex-col bg-white shadow-xl dark:bg-gray-900">
                    <div className="flex-1 h-0 overflow-y-auto">
                      <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            Map Layers
                          </Dialog.Title>
                          <div className="ml-3 h-7 flex items-center">
                            <button
                              type="button"
                              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                              onClick={onClose}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto">
                        <div className="px-4 sm:px-6">
                          <div>
                            <h2 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                              Layers
                            </h2>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-200">
                              Show/hide layers on the map. Note that some layers
                              will always be displayed depending on the context.
                            </p>

                            <ul className="mt-2 divide-y divide-gray-200 dark:divide-gray-500">
                              {layers.map(({ key, title, subtitle }) => (
                                <Switch.Group
                                  as="li"
                                  className="py-4 flex items-center justify-between"
                                  key={key}
                                >
                                  <div className="flex flex-col">
                                    <Switch.Label
                                      as="div"
                                      className="text-sm font-medium mb-1 text-gray-900 dark:text-white"
                                      passive
                                    >
                                      {title}
                                    </Switch.Label>
                                    <Switch.Description className="text-sm text-gray-400 dark:text-gray-200">
                                      {subtitle}
                                    </Switch.Description>
                                  </div>
                                  <Switch
                                    checked={visibleLayers[key]}
                                    onChange={() => {
                                      if (
                                        key ===
                                          VisualizationLayer.tooCloseToWitness &&
                                        !currentWorkspace?.permissions[
                                          "too-close-to-witness"
                                        ]
                                      ) {
                                        setModalUpgradeConfig({
                                          open: true,
                                          resolve: () => {
                                            setNewPlan((prevState) => ({
                                              ...prevState,
                                              plan: getPlanByType(PlanType.pro)
                                            }));
                                          },
                                          nextPlan: getPlanByType(PlanType.pro),
                                          featureTitle: "Too close to witness",
                                          featureDescription:
                                            "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                                        });

                                        return;
                                      }

                                      if (
                                        key ===
                                          VisualizationLayer.elevationContours &&
                                        !currentWorkspace?.permissions[
                                          "elevation-contours"
                                        ]
                                      ) {
                                        setModalUpgradeConfig({
                                          open: true,
                                          resolve: () => {
                                            setNewPlan((prevState) => ({
                                              ...prevState,
                                              plan: getPlanByType(PlanType.pro)
                                            }));
                                          },
                                          nextPlan: getPlanByType(PlanType.pro),
                                          featureTitle: "Elevation contours",
                                          featureDescription:
                                            "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                                        });

                                        return;
                                      }

                                      if (
                                        key === VisualizationLayer.satellite &&
                                        !currentWorkspace?.permissions[
                                          "satellite-view"
                                        ]
                                      ) {
                                        setModalUpgradeConfig({
                                          open: true,
                                          resolve: () => {
                                            setNewPlan((prevState) => ({
                                              ...prevState,
                                              plan: getPlanByType(PlanType.pro)
                                            }));
                                          },
                                          nextPlan: getPlanByType(PlanType.pro),
                                          featureTitle: "Satellite view",
                                          featureDescription:
                                            "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                                        });

                                        return;
                                      }

                                      trackEvent("toggle visualization layer", {
                                        event_category: "user settings",
                                        event_label: key,
                                        value: !visibleLayers[key] ? 1 : 0
                                      });
                                      toggleVisibleLayer(key);
                                    }}
                                    className={classNames(
                                      visibleLayers[key]
                                        ? "bg-hotspotty-400 dark:bg-hotspotty-200 dark:hover:bg-hotspotty-200"
                                        : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400",
                                      "ml-4 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-light-hotspotty-400"
                                    )}
                                  >
                                    <span className="sr-only">{title}</span>
                                    <span
                                      aria-hidden="true"
                                      className={classNames(
                                        visibleLayers[key]
                                          ? "translate-x-5"
                                          : "translate-x-0",
                                        "inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200"
                                      )}
                                    />
                                  </Switch>
                                </Switch.Group>
                              ))}
                            </ul>
                          </div>

                          <div className="space-y-6 mt-6">
                            <div>
                              <h2 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                Relative performance
                              </h2>
                              <p className="mt-1 text-sm text-gray-500 dark:text-gray-200">
                                Choose a metric to visualize the relative
                                performance of hotspots and simulated locations.
                              </p>
                            </div>

                            <div>
                              <label
                                htmlFor="location-dot-color"
                                className="text-sm font-medium text-gray-700 dark:text-white flex items-center"
                              >
                                <span className="mr-1">Locations</span>
                                <Tooltip
                                  placement="top"
                                  overlay="Currently the only available metric for coloring locations is the simulated transmit scale. Locations disabled for transmit scale simulations are grey."
                                >
                                  <InfoIcon className="inline-flex h-4 w-4" />
                                </Tooltip>
                              </label>
                              <div className="mt-1">
                                <select
                                  id="location-dot-color"
                                  className="w-full pl-3 py-2 sm:text-sm rounded-md cursor-not-allowed dark:bg-gray-900"
                                  value="Transmit scale"
                                  disabled
                                >
                                  <option value="Transmit scale">
                                    Simulated transmit scale
                                  </option>
                                </select>
                              </div>
                            </div>

                            <div>
                              <label
                                htmlFor="hotspot-dot-color"
                                className="block text-sm font-medium text-gray-700 dark:text-white"
                              >
                                Hotspots
                              </label>
                              <div className="mt-1">
                                <select
                                  id="hotspot-dot-color"
                                  className="w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
                                  onChange={(e) => {
                                    const metric = find(
                                      relativePerformanceMetrics,
                                      { key: e.target.value }
                                    );
                                    trackEvent("change hotspot colors", {
                                      event_category: "user settings",
                                      event_label: metric?.title as string
                                    });
                                    setPointColorMetricIteratee(e.target.value);
                                  }}
                                  value={pointColorMetricIteratee}
                                >
                                  {relativePerformanceMetrics.map(
                                    ({ key, title }) => (
                                      <option key={key} value={key}>
                                        {capitalize(title)}
                                      </option>
                                    )
                                  )}
                                </select>
                              </div>

                              <div className="w-full h-40 my-4">
                                {isHistogramDataLoading(
                                  histogramDataMap,
                                  pointColorMetricIteratee
                                ) ? (
                                  <CenteredLoading className="h-full" />
                                ) : (
                                  <HistogramChart
                                    data={
                                      histogramDataMap[pointColorMetricIteratee]
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MapAppearanceSettingsSidebar;
